<script>
/**
 * Card de insight da lateral do AAWZ IA Driven: o número que importa de um
 * módulo, com uma micro-visualização e uma legenda.
 *
 * Componente BURRO — recebe tudo por prop, não lê store nem `$access`. É o que
 * o mantém montável sem sessão no teste, mesma disciplina de `chat-sidebar.vue`
 * e `send-message.vue` (que recebem `agentes` já filtrados pelo pai).
 *
 * Cinco variantes, que são exatamente as que o protótipo desenha:
 *
 *   hero      → valor + legenda na linha, barra abaixo      (Consolidador)
 *   escala    → valor + trilho com marcador + bandas        (CRM)
 *   serie     → valor + legenda na linha, sparkline abaixo  (Financeiro, Comissões)
 *   progresso → "N de M" + barra + legenda                  (Tarefas)
 *   compacto  → um terço da linha, "N unidade" + indicador  (Compliance, Hub, …)
 *
 * Há ainda um TERCEIRO estado, além de carregando/carregado: `disponivel:
 * false` ("sem fonte de dado"). O endpoint real `/insights` devolve isso para
 * a maioria dos cards enquanto o backend não tem a fonte agregada por
 * escritório — decisão do usuário de colapsar o corpo da variante (nenhum
 * indicador gráfico) e mostrar só uma mensagem curta ("Em breve"), na MESMA
 * posição onde o valor real apareceria em cada variante — não uma posição
 * uniforme para todas. Duas rodadas de feedback (08-09 e 10-09/2026):
 * primeiro a mensagem ficou inline no rótulo pra TODAS as variantes (ruim no
 * `compacto`, que tem 3 cards por linha e ficava espremido); depois foi
 * movida pra uma linha própria abaixo do rótulo, também pra TODAS (o usuário
 * só queria isso pro `compacto`). Hoje: inline no rótulo para
 * `hero`/`escala`/`serie`/`progresso` (onde o valor já mora na linha do
 * rótulo ou logo abaixo dela), e numa linha própria abaixo do rótulo só para
 * `compacto` (onde o valor real também fica numa linha abaixo do título).
 * Ver `semFonteDeDado`/`mensagemSemDado`.
 *
 * As BANDAS da escala vêm por prop em vez de sair de `faixa-de-nota.js`
 * (fee-fixo): aquele util tem os rótulos de OUTRO domínio (Crítico/Atenção/
 * Bom/Excelente), e o CRM fala Ruim/Razoável/Boa/Ótima. Reusar a função traria
 * o vocabulário errado; reusar só a ideia mantém o componente genérico.
 */
import { TONS, tokenDoTom } from './tons'
import IaSparkline from './ia-sparkline.vue'
import {
  CAIXA,
  bandasPendentes,
  pontosPendentes,
  barrasPendentes,
  faixasPendentes,
} from './pendente'

const VARIANTES = ['hero', 'escala', 'serie', 'progresso', 'compacto']

// Os tons cuja cor é legível COMO TEXTO. `positivo-medio`/`positivo-fraco`
// mapeiam para `--success-bg-soft` (fundo com 15% de opacidade) e `neutro`
// para `--neutral-300` — os três somem num rótulo de 10px sobre a superfície
// quente da lateral. Ver o WHY em `corDaBandaAtiva`.
const TONS_LEGIVEIS_NA_BANDA = [TONS.POSITIVO, TONS.ALERTA, TONS.CRITICO]

// O verde de antes da cor por tom: o que a banda acesa mostrava sempre, e o
// que ela volta a mostrar quando o tom não é legível ou não há julgamento.
const COR_PADRAO_DA_BANDA = 'var(--success-text)'

const TAMANHO_CHEVRON = 12

// Motivo (do backend) → mensagem curta e discreta. Motivo desconhecido (ou
// ausente) cai no padrão — nunca afirma "R$ 0": o dado não é zero, é
// simplesmente uma fonte que ainda não existe.
const MENSAGEM_POR_MOTIVO = {
  sem_fonte_agregada_por_escritorio: 'Em breve',
}
const MENSAGEM_SEM_DADO_PADRAO = 'Em breve'

export default {
  name: 'IaInsightCard',
  components: { IaSparkline },
  props: {
    variante: {
      type: String,
      default: 'compacto',
      validator: (v) => VARIANTES.indexOf(v) !== -1,
    },
    rotulo: { type: String, required: true },
    // Já FORMATADO pelo pai ("R$ 48,2 M", "Boa", "4"). O card não formata: quem
    // sabe se o número é dinheiro, nota ou contagem é a fonte, não a casca.
    valor: { type: String, default: '' },
    // `compacto`: o que o número conta ("clientes pendentes").
    unidade: { type: String, default: '' },
    // Trecho colorido da legenda ("+2,1%", "3 atividades críticas").
    destaque: { type: String, default: '' },
    tomDestaque: {
      type: String,
      default: TONS.NEUTRO,
      validator: (v) => Object.values(TONS).indexOf(v) !== -1,
    },
    legenda: { type: String, default: '' },
    // Mostra o chevron e torna o card clicável.
    navegavel: { type: Boolean, default: false },
    // `hero` e `compacto`: faixas proporcionais — [{ peso, tom }].
    segmentos: { type: Array, default: () => [] },
    // `escala`: { posicao: 0-100, bandas: [String], ativa: Number }
    escala: { type: Object, default: null },
    // `serie`: pontos do sparkline.
    serie: { type: Array, default: () => [] },
    // `progresso`: { feito, total }
    progresso: { type: Object, default: null },
    // `compacto`: severidades, uma por ponto — ['critico','critico','alerta']
    pontos: { type: Array, default: () => [] },
    // `compacto`: [{ altura: 0-100, tom }]
    barras: { type: Array, default: () => [] },
    // ★ Carregando é o PRÓPRIO card com os números pendentes, não um bloco
    // cinza no lugar dele: o rótulo e a régua vêm do catálogo e já são
    // conhecidos. Por isso o estado mora aqui dentro e não num componente
    // irmão — o `<style scoped>` não atravessaria, e as duas árvores
    // divergiriam até o esqueleto virar um retângulo genérico.
    carregando: { type: Boolean, default: false },
    // Geometria que só o esqueleto usa: `{ bandas, pontos, barras }` do
    // catálogo. Ver `pendente.js`.
    esqueleto: { type: Object, default: null },
    // `false` quando o backend ainda não tem a fonte agregada por escritório
    // para este card — colapsa o corpo inteiro (ver `semFonteDeDado`). O caso
    // comum é "disponível" (7 dos 9 cards do catálogo hoje NÃO mandam esta
    // prop e precisam continuar desenhando normalmente), por isso o default é
    // `true` — contraria `vue/no-boolean-default` conscientemente, mesmo
    // precedente de `fp-mobile-sheet.vue`.
    // eslint-disable-next-line vue/no-boolean-default
    disponivel: { type: Boolean, default: true },
    // Motivo mandado pelo backend (`sem_fonte_agregada_por_escritorio`, ...).
    // Vira mensagem via `MENSAGEM_POR_MOTIVO`; desconhecido cai no padrão.
    motivo: { type: String, default: '' },
  },
  data() {
    return { TAMANHO_CHEVRON, CAIXA }
  },
  computed: {
    classeRaiz() {
      return [
        'ia-insight',
        'ia-insight--' + this.variante,
        this.navegavel ? 'ia-insight--navegavel' : '',
      ]
    },
    ehProgresso() {
      return this.variante === 'progresso'
    },
    // Sem fonte de dado: some o corpo inteiro da variante (sparkline/trilho/
    // barra/pontos/barrinhas) e vira uma linha única com a mensagem à direita.
    semFonteDeDado() {
      return this.disponivel === false
    },
    mensagemSemDado() {
      return MENSAGEM_POR_MOTIVO[this.motivo] || MENSAGEM_SEM_DADO_PADRAO
    },
    /**
     * Variantes que sobem valor e legenda para a linha do RÓTULO.
     *
     * ★ Economia de uma linha por card (28/08). A linha do rótulo é baixa (11px
     * em caixa alta) e sobrava vazia da metade para a direita; o valor cabe ali
     * sem apertar nada, e o indicador — trilho — passa a ser a segunda e
     * última linha.
     *
     * O `hero` fica de fora de propósito: 26px na linha do rótulo deixaria de
     * ser manchete e viraria mais um dado ao lado de um título.
     *
     * ⚠️ `serie` SAIU daqui (06/09 — pedido do usuário, com print de
     * referência): o valor volta a ter linha própria, ABAIXO do rótulo, e o
     * sparkline divide a linha seguinte com a legenda em vez de ocupar a
     * largura inteira do card sozinho — ver o template do `serie` mais
     * abaixo. Reverte a decisão de 28/08 só para esta variante.
     */
    valorNoRotulo() {
      return this.variante === 'escala'
    },
    corDoDestaque() {
      return tokenDoTom(this.tomDestaque)
    },
    /**
     * Cor do rótulo da faixa em que o dado caiu.
     *
     * ★ Era `--success-text` fixo no Sass — VERDE sempre, inclusive quando a
     * medida era ruim. Com o trilho neutro e o marcador escuro (decisões de
     * 28/08), o rótulo aceso é a ÚNICA cor do bloco: pintá-lo de verde numa
     * nota "Ruim" faz a régua dar o veredito errado, que é exatamente o que
     * aquelas duas decisões quiseram evitar. Medido em 15/09/2026 — "Razoável"
     * saía verde com 9 de 13 clientes sem contato em dia.
     *
     * Usa o MESMO `tomDestaque` que o backend já manda para o destaque, então
     * as duas cores do card sempre concordam. Sem tom declarado, mantém o
     * verde de antes — nenhuma escala existente muda de cor sem o backend
     * pedir.
     */
    corDaBandaAtiva() {
      // ⚠️ Só os três tons LEGÍVEIS COMO TEXTO entram, e isso não é
      // conservadorismo: `positivo-medio` e `positivo-fraco` são valores
      // DECLARADOS no contrato (`api/apiInsights.js`) e caem em
      // `--success-bg-soft` = `rgba(57, 142, 74, 0.15)` — um token de FUNDO.
      // Num rótulo de 10px sobre `--sidebar-warm-bg` isso dá ~1.1:1 de
      // contraste: invisível. O mesmo vale para `neutro` (`--neutral-300`,
      // ~1.4:1).
      //
      // Antes desta cor por tom a banda era SEMPRE legível; deixar o payload
      // escolher livremente trocaria um defeito (verde em nota ruim) por
      // outro (rótulo que some). Fora dos três, volta ao verde de antes —
      // que é também o que "sem julgamento" deve mostrar.
      return TONS_LEGIVEIS_NA_BANDA.indexOf(this.tomDestaque) >= 0
        ? tokenDoTom(this.tomDestaque)
        : COR_PADRAO_DA_BANDA
    },
    // Fundo da pílula do `serie` (v-chip): o MESMO token de `corDoDestaque`,
    // diluído — nunca um hex novo. Mesma receita do `.ia-sparkline__topo`
    // (`color-mix`), pela mesma razão: token pronto (`--success-bg-soft`)
    // não existe pra todo tom (`alerta`/`critico`), e a pílula precisa da
    // MESMA família de cor do texto, só mais clara.
    fundoDoDestaque() {
      return 'color-mix(in srgb, ' + this.corDoDestaque + ' 16%, var(--white))'
    },
    // Faixas do `hero`/`compacto` normalizadas: peso não-positivo é descartado
    // (um `flex: 0` seria uma faixa invisível ocupando lugar no DOM) e cada uma
    // ganha a cor do seu tom.
    faixas() {
      if (this.carregando) return faixasPendentes()
      const validas = (this.segmentos || []).filter((s) => {
        return s && Number(s.peso) > 0
      })
      return validas.map((s, indice) => ({
        chave: indice,
        estilo: { flex: Number(s.peso), background: tokenDoTom(s.tom) },
      }))
    },
    // ⚠️ Preso a 0-100. Sem o clamp, uma nota fora da faixa joga o marcador
    // para fora do trilho — e o dado real vem de fonte que a casca não controla.
    posicaoDoMarcador() {
      if (!this.escala) return 0
      const bruta = Number(this.escala.posicao)
      if (!isFinite(bruta)) return 0
      return Math.min(100, Math.max(0, bruta))
    },
    bandas() {
      // A régua é estrutura, não medição: os rótulos aparecem de verdade
      // durante o carregando, só que sem nenhum marcado como ativo.
      if (this.carregando) return bandasPendentes(this.esqueleto)
      if (!this.escala) return []
      const ativa = Number(this.escala.ativa)
      // ★ O ESTILO vem pré-computado aqui, como em `faixas()` — o template só
      // faz `:style="b.estilo"`. Antes o ternário morava no template
      // (`b.ativa ? {...} : null`) e o binding era inauditável: apagar a linha
      // inteira devolvia o bug visual com os testes VERDES (medido por mutação
      // no review do PR #2289). Com o estilo no computed, o teste afere
      // `bandas[i].estilo` e cobre os dois ramos.
      return (this.escala.bandas || []).map((rotulo, indice) => ({
        rotulo,
        ativa: indice === ativa,
        estilo: indice === ativa ? { color: this.corDaBandaAtiva } : null,
      }))
    },
    // Mesmo clamp do marcador, pelo mesmo motivo — e `total` zero não pode
    // virar divisão por zero.
    percentualConcluido() {
      if (!this.progresso) return 0
      const total = Number(this.progresso.total)
      const feito = Number(this.progresso.feito)
      if (!isFinite(total) || total <= 0) return 0
      if (!isFinite(feito) || feito <= 0) return 0
      return Math.min(100, Math.round((feito / total) * 100))
    },
    larguraDoProgresso() {
      return { width: this.percentualConcluido + '%' }
    },
    // A barra do progresso fica no trilho vazio: preenchê-la em qualquer
    // percentual afirmaria um avanço que ninguém mediu.
    larguraPendente() {
      return this.carregando ? { width: '0%' } : this.larguraDoProgresso
    },
    pontosDecorados() {
      if (this.carregando) return pontosPendentes(this.esqueleto)
      return (this.pontos || []).map((tom, indice) => ({
        chave: indice,
        estilo: { background: tokenDoTom(tom) },
      }))
    },
    // A altura vira percentual do trilho; abaixo de 1 a barra some, então o
    // piso é 1 — barra invisível parece dado faltando, não dado baixo.
    barrasDecoradas() {
      if (this.carregando) return barrasPendentes(this.esqueleto)
      return (this.barras || []).map((b, indice) => {
        const altura = Number((b || {}).altura)
        const util = isFinite(altura) ? Math.min(100, Math.max(1, altura)) : 1
        return {
          chave: indice,
          estilo: { height: util + '%', background: tokenDoTom((b || {}).tom) },
        }
      })
    },
  },
  methods: {
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
  },
}
</script>

<template>
  <component
    :is="navegavel ? 'button' : 'div'"
    :class="classeRaiz"
    :type="navegavel ? 'button' : null"
    @click="aoClicar"
  >
    <div class="ia-insight__rotulo ia-texto-selo">
      <span>{{ rotulo }}</span>
      <!-- ⚠️ `arrow-right` NÃO existe na lib (nome inexistente renderiza vazio,
           sem erro). O `arrow-left` é que aponta para a DIREITA — mal nomeado,
           armadilha já registrada em `cliente-sidebar.vue`, que o ESPELHA para
           virar seta de voltar. Aqui queremos a direção nativa dele. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <!-- Sem fonte de dado: nas variantes que já mostram o valor real na
           linha do rótulo (`escala`) ou logo abaixo dela (`hero`/`serie`/
           `progresso`), a mensagem também fica AQUI, inline — só o `compacto`
           (onde o valor mora numa linha abaixo do título) ganha a mensagem
           numa linha própria, fora do rótulo (ver o corpo do card, mais
           abaixo). Por isso vem ANTES de `ehProgresso`/`valorNoRotulo`: sem
           fonte de dado, nenhum dos dois teria o que mostrar mesmo. -->
      <span
        v-if="semFonteDeDado && variante !== 'compacto'"
        class="ia-insight__sem-dado ia-texto-auxiliar ia-insight__sem-dado--inline"
        >{{ mensagemSemDado }}</span
      >
      <!-- No `progresso` o contador divide a linha com o rótulo, como no
           protótipo — empilhado ele gastava uma linha inteira da lateral. -->
      <span v-else-if="!semFonteDeDado && ehProgresso" class="ia-insight__contador ia-texto-auxiliar">
        <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.contador" />
        <template v-else>{{ valor }}</template>
      </span>
      <!-- `escala` e `serie`: valor e legenda moram AQUI, encostados à direita.
           Ver `valorNoRotulo` para o porquê. -->
      <span
        v-else-if="!semFonteDeDado && valorNoRotulo"
        class="ia-insight__resumo"
      >
        <span class="ia-insight__valor ia-texto-numero-destaque">
          <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.valor" />
          <template v-else>{{ valor }}</template>
        </span>
        <span class="ia-insight__aux ia-texto-auxiliar">
          <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.aux" />
          <template v-else>
            <strong v-if="destaque" :style="{ color: corDoDestaque }">{{
              destaque
            }}</strong>
            {{ legenda }}
          </template>
        </span>
      </span>
    </div>

    <!-- Sem fonte de dado: NENHUM corpo de variante — sparkline, trilho,
         barra, pontos, barrinhas e faixas variam demais entre si para valer a
         pena desenhar uma versão "achatada" de cada um (decisão do usuário,
         08-09/2026). Nas variantes `hero`/`escala`/`serie`/`progresso` a
         mensagem já foi mostrada INLINE, na linha do rótulo (ver acima) —
         aqui elas não renderizam corpo nenhum. Só o `compacto` ganha uma
         linha PRÓPRIA com a mensagem, abaixo do rótulo: é a MESMA posição em
         que o valor real aparece nele (uma linha abaixo do título), e é a
         única variante que fica espremida com a mensagem inline por dividir
         a linha com outros 2 cards (decisão do usuário, duas rodadas —
         08-09 e 10-09/2026: a 1ª tentativa pôs a mensagem inline em TODAS as
         variantes, a 2ª moveu para uma linha abaixo em TODAS; o usuário
         corrigiu para só o `compacto` usar a linha abaixo). -->
    <template v-if="!semFonteDeDado">
    <!-- hero: o número é o assunto do bloco.
         ★ Valor e legenda dividem UMA linha (28/08), com a barra em largura
         cheia abaixo. Empilhados eram três linhas para dizer uma coisa, e o
         hero é o card que mais custa altura na lateral. A legenda vai para a
         direita — onde o olho já cai depois de percorrer o número. -->
    <template v-if="variante === 'hero'">
      <div class="ia-insight__linha ia-insight__linha--entre">
        <span class="ia-insight__valor ia-insight__valor--hero ia-texto-titulo-cartao">
          <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.valorHero" />
          <template v-else>{{ valor }}</template>
        </span>
        <span v-if="carregando" class="ia-insight__aux ia-texto-auxiliar">
          <BaseLoadingSkeleton v-bind="CAIXA.aux" />
        </span>
        <span v-else-if="destaque || legenda" class="ia-insight__aux ia-texto-auxiliar">
          <strong v-if="destaque" :style="{ color: corDoDestaque }">{{
            destaque
          }}</strong>
          {{ legenda }}
        </span>
      </div>
      <div v-if="faixas.length" class="ia-insight__faixas">
        <span v-for="f in faixas" :key="f.chave" :style="f.estilo" />
      </div>
    </template>

    <!-- escala: onde o indicador cai numa régua qualitativa. O valor e a
         legenda ficam na linha do rótulo (ver `valorNoRotulo`). -->
    <template v-else-if="variante === 'escala'">
      <div
        class="ia-insight__trilho"
        :class="{ 'ia-insight__trilho--pendente': carregando }"
      >
        <!-- Marcador OCULTO enquanto carrega: em qualquer posição ele seria uma
             afirmação sobre uma nota que ainda não chegou. O trilho vazio diz
             "ainda não"; o marcador em 0 diria "péssimo". -->
        <span
          v-if="!carregando"
          class="ia-insight__marcador"
          :style="{ left: posicaoDoMarcador + '%' }"
        />
      </div>
      <div class="ia-insight__bandas ia-texto-micro">
        <span
          v-for="b in bandas"
          :key="b.rotulo"
          :class="{ 'ia-insight__banda--ativa': b.ativa }"
          :style="b.estilo"
          >{{ b.rotulo }}</span
        >
      </div>
    </template>

    <!-- serie: DUAS colunas (06/09, 3ª rodada — "deveria ter duas colunas,
         uma somente para o gráfico"). Coluna esquerda (`__serie-texto`):
         valor+destaque numa linha, legenda na linha de baixo. Coluna direita
         (`__serie-grafico`): só o sparkline, metade da largura do card,
         alinhado ao TOPO da coluna de texto — é o que faz o gráfico "subir"
         em vez de ficar preso à altura só da legenda. -->
    <template v-else-if="variante === 'serie'">
      <div class="ia-insight__serie-corpo">
        <div class="ia-insight__serie-texto">
          <div class="ia-insight__linha">
            <span class="ia-insight__valor ia-texto-numero-destaque">
              <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.valor" />
              <template v-else>{{ valor }}</template>
            </span>
            <span v-if="carregando" class="ia-insight__aux ia-texto-auxiliar">
              <BaseLoadingSkeleton v-bind="CAIXA.auxSerieDestaque" />
            </span>
            <!-- `v-chip`, não `<strong>` (pedido do usuário, com print do
                 protótipo): o destaque vira pílula colorida, igual ao badge
                 de "+6,2%"/"+9,4%" do protótipo. Fundo é o MESMO tom do texto,
                 só diluído (`color-mix`) — nunca hex novo. -->
            <v-chip
              v-else-if="destaque"
              x-small
              class="ia-insight__serie-destaque"
              :style="{ color: corDoDestaque, background: fundoDoDestaque }"
              >{{ destaque }}</v-chip
            >
          </div>
          <span v-if="carregando" class="ia-insight__aux ia-texto-auxiliar">
            <BaseLoadingSkeleton v-bind="CAIXA.auxSerieLegenda" />
          </span>
          <span v-else-if="legenda" class="ia-insight__aux ia-texto-auxiliar">{{ legenda }}</span>
        </div>
        <IaSparkline
          class="ia-insight__serie-grafico"
          :serie="serie"
          :fantasma="carregando"
        />
      </div>
    </template>

    <!-- progresso: quanto do dia já foi -->
    <template v-else-if="variante === 'progresso'">
      <div
        class="ia-insight__barra"
        :class="{ 'ia-insight__barra--pendente': carregando }"
      >
        <span :style="larguraPendente" />
      </div>
      <div v-if="carregando" class="ia-insight__aux ia-texto-auxiliar">
        <BaseLoadingSkeleton v-bind="CAIXA.aux" />
      </div>
      <div v-else-if="destaque || legenda" class="ia-insight__aux ia-texto-auxiliar">
        <strong v-if="destaque" :style="{ color: corDoDestaque }">{{
          destaque
        }}</strong>
        {{ legenda }}
      </div>
    </template>

    <!-- compacto: meia largura, um número e um sinal -->
    <template v-else>
      <div class="ia-insight__valor ia-texto-numero-destaque">
        <BaseLoadingSkeleton v-if="carregando" v-bind="CAIXA.valor" />
        <template v-else>
          {{ valor }}
          <span v-if="unidade" class="ia-insight__unidade ia-texto-auxiliar">{{ unidade }}</span>
        </template>
      </div>
      <div
        v-if="pontosDecorados.length"
        class="ia-insight__pontos"
        :class="{ 'ia-insight__pontos--pendente': carregando }"
      >
        <span v-for="p in pontosDecorados" :key="p.chave" :style="p.estilo" />
      </div>
      <div
        v-else-if="barrasDecoradas.length"
        class="ia-insight__barrinhas"
        :class="{ 'ia-insight__barrinhas--pendente': carregando }"
      >
        <span v-for="b in barrasDecoradas" :key="b.chave" :style="b.estilo" />
      </div>
      <div v-else-if="faixas.length" class="ia-insight__faixas">
        <span v-for="f in faixas" :key="f.chave" :style="f.estilo" />
      </div>
    </template>
    </template>
    <div
      v-else-if="variante === 'compacto'"
      class="ia-insight__sem-dado ia-texto-auxiliar"
    >
      {{ mensagemSemDado }}
    </div>
  </component>
</template>

<style lang="sass" scoped>
.ia-insight
  // [feedback do usuário, 06/09] "deveria estar usando Geist, no padrão" — a
  // pilha do `body` (`design/main.sass`) tenta 'Open Sans' ANTES de 'Geist',
  // então sem isto o card herdava a fonte errada quando Open Sans está
  // disponível no sistema. Explícito aqui, como o guia do DS pede ("corpo/
  // dados/rótulos em Geist, consuma sempre via var"), em vez de depender da
  // ordem de fallback do body.
  font-family: var(--font-family-paragraph)
  display: flex
  flex-direction: column
  // ★ 10px (era 8): o valor perdeu peso (700 → 500) e quem devolve a hierarquia
  // é o ar. Baixar o peso sem abrir o respiro só apaga o número.
  //
  // ⚠️ O `padding` daqui NÃO vale na lateral: `::v-deep .ia-insight:not(...)`
  // no `chat-sidebar` tem especificidade de pai e vence. Respiro entre blocos é
  // de lá; daqui sai só o ritmo INTERNO do card.
  gap: 10px
  width: 100%
  padding: 14px 0
  text-align: left
  background: transparent
  border-bottom: 1px solid var(--sidebar-warm-border)

.ia-insight--navegavel
  cursor: pointer

  // ── Estado de carregando ────────────────────────────────────────────────
  // Os retângulos de 6px do `BaseLoadingSkeleton` empilhados leem como
  // formulário. Em pílula eles leem como TEXTO ausente, que é o que são.
  ::v-deep .base-loading-skeleton,
  ::v-deep .base-loading-skeleton::before
    border-radius: 999px

  &:hover .ia-insight__rotulo
    color: var(--neutral-800)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// O `compacto` divide a linha com um irmão: sem borda própria (quem a desenha é
// o par, no `chat-sidebar`) e sem o padding vertical do bloco inteiro.
.ia-insight--compacto
  gap: 7px
  padding: 0
  border-bottom: none

.ia-insight__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  // Caixa alta em 11px precisa de mais entreletra que texto normal — e aqui ela
  // também compensa o peso que saiu do valor logo abaixo.
  letter-spacing: 0.06em
  text-transform: uppercase

.ia-insight__valor
  color: var(--neutral-900)
  // Dígito de largura fixa: sem isto o número "pula" de largura a cada
  // atualização quando o dado virar tempo real.
  font-variant-numeric: tabular-nums

// O valor em MANCHETE é o papel `titulo-cartao` (22/28/500); os outros valores
// são `numero-destaque` (17/24/500). A cor (`--neutral-900`) vem de
// `.ia-insight__valor`, que as duas variantes compartilham.
.ia-insight__valor--hero
  letter-spacing: -0.01em

.ia-insight__contador
  color: var(--neutral-700)
  margin-left: auto
  letter-spacing: 0
  text-transform: none
  font-variant-numeric: tabular-nums

// A variante `--inline` (hero/escala/serie/progresso) vive DENTRO do rótulo —
// precisa desfazer caixa-alta/entreletra/cor do `.ia-insight__rotulo` pai,
// mesmo reset que `.ia-insight__contador`/`.ia-insight__resumo` já fazem.
// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.ia-insight__sem-dado
  color: var(--neutral-600)

.ia-insight__sem-dado--inline
  margin-left: auto
  letter-spacing: 0
  text-transform: none

.ia-insight__linha
  display: flex
  align-items: baseline
  gap: 8px

.ia-insight__linha--entre
  justify-content: space-between

  // O número manda na linha: quem cede espaço é a legenda, e ela cede pela
  // direita porque é lá que ela termina. Sem o `min-width: 0` o texto se recusa
  // a encolher e empurra o valor para fora do card.
  .ia-insight__valor
    flex-shrink: 0

  .ia-insight__aux
    min-width: 0
    text-align: right
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

// Valor + legenda encostados à direita na linha do rótulo (`escala`).
//
// ⚠️ Precisa DESFAZER o que `.ia-insight__rotulo` impõe: caixa alta, entreletra
// e o cinza de rótulo. Sem isso "Boa" saía em maiúsculas espaçadas.
// Mesmo reset que o `__contador` do progresso já fazia, pelo mesmo motivo.
.ia-insight__resumo
  display: flex
  align-items: baseline
  gap: 8px
  margin-left: auto
  min-width: 0
  letter-spacing: 0
  text-transform: none
  // `--neutral-800`, não o `--neutral-900` do valor em linha própria: aqui ele
  // é a informação de apoio do rótulo, não a manchete do bloco.
  color: var(--neutral-800)

  // ⚠️ MENOR que o valor das outras variantes (13 contra 17, 11 contra 12).
  // Ali ele é o assunto de uma linha própria; aqui divide a linha com o rótulo
  // do módulo, e no tamanho cheio passava a pesar mais que o título ao lado —
  // o card ficava com dois assuntos disputando a mesma régua.
  //
  // O número manda dentro do resumo: quem cede espaço é a legenda, truncando
  // pela direita.
  .ia-insight__valor
    flex-shrink: 0
    font-size: var(--ia-font-corpo)

  .ia-insight__aux
    min-width: 0
    font-size: var(--ia-font-auxiliar)
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  // ★ O destaque perde o negrito do `<strong>` (700 → 500). Quem sinaliza aqui
  // é a COR — o verde do positivo, o âmbar do alerta —, e somar peso a ela era
  // dizer a mesma coisa duas vezes. Numa coluna com nove cards, nove trechos em
  // negrito colorido faziam a lateral inteira pedir atenção ao mesmo tempo.
  //
  // Continua `<strong>` no HTML: a ênfase é semântica e vale para leitor de
  // tela; o que muda é só como ela se apresenta.
  strong
    font-weight: 500

// Destaque do `serie`: pílula (`v-chip`), não mais `<strong>` — pedido do
// usuário com print do protótipo. Reset do que o Vuetify aplica por padrão
// (margem própria, fundo do tema) — cor e fundo REAIS vêm do `:style` inline
// (`corDoDestaque`/`fundoDoDestaque`, no script), nunca hex aqui.
//
// ⚠️ `height: 18px`, NÃO `auto` (achado do usuário — "o texto ficou mais
// distante, talvez pelo v-chip?"). Com `auto`, o chip crescia pro conteúdo +
// padding (~22px, MAIOR que os 17px do valor do lado) e empurrava a linha
// INTEIRA (`.ia-insight__linha`) pra essa altura — a legenda embaixo, então,
// tinha 5px de gap A MAIS do que o declarado, só porque a linha de cima
// cresceu. `18px` fica raso o bastante pra não vencer o valor.
.ia-insight__serie-destaque
  margin: 0
  height: 18px
  padding: 0 8px
  border-radius: 999px
  font-size: var(--ia-font-selo)
  font-weight: 600
  align-self: center

  ::v-deep .v-chip__content
    padding: 0

// [feedback do usuário, 06/09, 3ª rodada] "deveria ter duas colunas, uma
// somente para o gráfico" — a linha ÚNICA (texto+gráfico) fazia o sparkline
// centralizar (`align-items: center`) contra só a ALTURA DA LEGENDA, e ficava
// "preso" mais para baixo do que o valor logo acima. Virou duas COLUNAS:
// `__serie-texto` (valor+destaque, legenda embaixo) e `__serie-grafico`
// (só o sparkline), lado a lado, alinhadas pelo TOPO — o gráfico sobe até a
// altura do valor, em vez de responder só à legenda.
.ia-insight__serie-corpo
  display: flex
  // `flex-end` (era `flex-start`) — [feedback do usuário, 4ª rodada] "faça
  // o gráfico subir mais, pra ver como ficaria": com o gráfico mais alto que
  // a coluna de texto (ver `.ia-insight__serie-grafico` abaixo), alinhar
  // pelo TOPO deixaria sobrar espaço embaixo; pelo FUNDO, o topo do gráfico é
  // que sobe além do valor.
  align-items: flex-end
  justify-content: space-between
  gap: 12px

.ia-insight__serie-texto
  display: flex
  flex-direction: column
  // [feedback do usuário, 06/09, 5ª rodada] "esses 3 componentes deveriam
  // ter a mesma distância, o título tá mais distante dos demais" — 10px,
  // o MESMO valor do `gap` de `.ia-insight` (pai, entre rótulo e este bloco),
  // pra rótulo → valor e valor → legenda ficarem espaçados igual.
  gap: 10px
  min-width: 0

  .ia-insight__aux
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

// `flex: 0 0 50%`, não `width: 50%`: junto com o `.ia-sparkline` interno
// (`width: 100%`, ver `ia-sparkline.vue`), é o que trava o SVG em exatamente
// metade do card sem depender de encolhimento do irmão (o texto) para
// sobrar espaço.
.ia-insight__serie-grafico
  flex: 0 0 50%
  max-width: 50%
  // [feedback do usuário, 06/09, 6ª rodada] "o gráfico tá ficando muito
  // pequeno no eixo Y, deveria se expandir mais pra cima" — 48px (a coluna de
  // texto ao lado, com os 3 gaps iguais de 10px, mede ~41px; o gráfico fica
  // um pouco MAIOR que ela de propósito, pro traço ter corpo). Compound
  // selector (`.ia-sparkline`, a classe que o PRÓPRIO `ia-sparkline.vue` já
  // aplica no `<svg>`) pra vencer por especificidade o `height: 30px` de lá,
  // sem tocar no componente compartilhado — o OUTRO consumidor
  // (`ia-cliente-carteira.vue`) não passa esta classe e continua nos 30px.
  &.ia-sparkline
    height: 48px

// Faixas proporcionais (hero / compacto).
.ia-insight__faixas
  display: flex
  gap: 3px
  // 6px (era 5): com o valor mais leve acima, a barra fina lia como risco solto.
  height: 6px

  span
    border-radius: 3px

// ★ Trilho NEUTRO (28/08). Era um degradê `neutral-200 → success`, que pintava
// de verde o lado bom da régua o tempo todo — a escala parecia estar dando um
// veredito antes de olhar o marcador. Cinza chapado deixa a régua ser régua: a
// única cor do bloco passa a ser o rótulo da faixa em que o dado caiu.
//
// `--neutral-300` e não `--neutral-200`: o 200 some sobre a superfície quente
// da lateral (medido). O degradê disfarçava isso porque terminava em verde; sem
// ele, o trilho precisa do tom que se enxerga sozinho.
.ia-insight__trilho
  position: relative
  height: 4px
  border-radius: 2px
  background: var(--neutral-300)

// `ia-shimmer` é a keyframe GLOBAL de `design/_ia-shimmer.sass` — pulso de
// opacidade, que compõe na GPU; um shimmer de `background-position` repintaria
// por quadro nesta lateral que re-renderiza enquanto carrega, e leria como
// travamento.
//
// Só a animação: o trilho já nasce neutro, então não há mais o que desligar
// aqui — antes esta regra existia para apagar o verde do degradê.
.ia-insight__trilho--pendente
  animation: ia-shimmer 1.4s ease-in-out infinite

// ★ RISCO, não bola (28/08). A bola verde de 10px sobre um trilho de 4px era o
// elemento mais pesado do card e repetia em cor o que o rótulo aceso já dizia.
// O risco escuro aponta a posição sem opinar sobre ela — é agulha de régua.
//
// O anel da cor da superfície separa o risco do trilho; sem ele os dois se
// fundem num borrão escuro nesta espessura.
.ia-insight__marcador
  position: absolute
  top: 50%
  width: 2px
  height: 10px
  border-radius: 1px
  transform: translate(-50%, -50%)
  background: var(--neutral-900)
  box-shadow: 0 0 0 2px var(--sidebar-warm-bg)

.ia-insight__bandas
  color: var(--neutral-600)
  display: flex
  justify-content: space-between

// ⚠️ SEM `color` aqui: quem pinta a banda acesa é o `:style` do computed
// `bandas()`, que sempre vence o Sass. A declaração que existia era MORTA —
// `bandasPendentes` devolve `ativa: false` em toda banda (`pendente.js`), então
// a classe nunca aparece sem o inline junto. Mantê-la fazia quem editasse o
// Sass mudar nada e quem lesse achar que a banda é verde por aqui.
.ia-insight__banda--ativa
  font-weight: 600

// ⚠️ O `v-sparkline` desenha um SVG RESPONSIVO (`width: 100%`): as props
.ia-insight__barra
  height: 6px
  border-radius: 3px
  background: var(--neutral-200)
  overflow: hidden

  span
    display: block
    height: 100%
    border-radius: 3px
    background: var(--success)

.ia-insight__barra--pendente,
.ia-insight__pontos--pendente,
.ia-insight__barrinhas--pendente
  animation: ia-shimmer 1.4s ease-in-out infinite

.ia-insight__pontos
  display: flex
  gap: 4px

  // ★ Círculo de 8px (era quadrado de 10 com raio 3). O quadradinho lia como
  // caixa de status de tabela; redondo lê como contagem de itens, que é o que
  // ele conta. Menor porque o círculo tem mais área visual que o quadrado no
  // mesmo lado.
  span
    width: 8px
    height: 8px
    border-radius: 50%

.ia-insight__barrinhas
  display: flex
  align-items: flex-end
  gap: 3px
  height: 10px

  span
    width: 6px
    border-radius: 1px

@media (prefers-reduced-motion: reduce)
  .ia-insight__trilho--pendente,
  .ia-insight__barra--pendente,
  .ia-insight__pontos--pendente,
  .ia-insight__barrinhas--pendente
    animation: none

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.ia-insight__unidade
  color: var(--neutral-700)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.ia-insight__aux
  color: var(--neutral-700)

</style>
